import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Plus, Check, X, Coins, Clock, CheckCircle2, XCircle, Trash2, Edit3, ShoppingBag, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RewardsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('browse');
  const [rewards, setRewards] = useState([]);
  const [browseData, setBrowseData] = useState({ rewards: [], my_requests: {} });
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [presets, setPresets] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [newReward, setNewReward] = useState({ name_en: '', name_bm: '', cost: 100, emoji: '🎁', category: 'custom' });
  const [gData, setGData] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const isEN = language === 'en';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchAll();
  }, [token]);

  const fetchAll = async () => {
    try {
      const [rwRes, brRes, reqRes, myReqRes, preRes, gRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/rewards`, { headers }),
        fetch(`${BACKEND_URL}/api/rewards/browse`, { headers }),
        fetch(`${BACKEND_URL}/api/rewards/requests`, { headers }),
        fetch(`${BACKEND_URL}/api/rewards/my-requests`, { headers }),
        fetch(`${BACKEND_URL}/api/rewards/presets`),
        fetch(`${BACKEND_URL}/api/gamification/me`, { headers }),
      ]);
      if (rwRes.ok) setRewards(await rwRes.json());
      if (brRes.ok) setBrowseData(await brRes.json());
      if (reqRes.ok) setRequests(await reqRes.json());
      if (myReqRes.ok) setMyRequests(await myReqRes.json());
      if (preRes.ok) setPresets(await preRes.json());
      if (gRes.ok) setGData(await gRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const addPreset = async (presetId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/rewards/preset/${presetId}`, { method: 'POST', headers });
      if (res.ok) { showToast(isEN ? 'Reward added!' : 'Ganjaran ditambah!'); fetchAll(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', 'error'); }
    } catch { showToast('Error', 'error'); }
  };

  const createReward = async () => {
    if (!newReward.name_en.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/rewards`, { method: 'POST', headers, body: JSON.stringify(newReward) });
      if (res.ok) {
        showToast(isEN ? 'Reward created!' : 'Ganjaran dicipta!');
        setShowCreate(false);
        setNewReward({ name_en: '', name_bm: '', cost: 100, emoji: '🎁', category: 'custom' });
        fetchAll();
      } else { const e = await res.json(); showToast(e.detail || 'Error', 'error'); }
    } catch { showToast('Error', 'error'); }
  };

  const deleteReward = async (rewardId) => {
    try {
      await fetch(`${BACKEND_URL}/api/rewards/${rewardId}`, { method: 'DELETE', headers });
      showToast(isEN ? 'Deleted' : 'Dipadam');
      fetchAll();
    } catch { }
  };

  const requestReward = async (rewardId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/rewards/request/${rewardId}`, { method: 'POST', headers });
      if (res.ok) { showToast(isEN ? 'Requested! Waiting for approval.' : 'Diminta! Menunggu kelulusan.'); fetchAll(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', 'error'); }
    } catch { showToast('Error', 'error'); }
  };

  const approveRequest = async (requestId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/rewards/requests/${requestId}/approve`, { method: 'POST', headers });
      if (res.ok) { showToast(isEN ? 'Approved!' : 'Diluluskan!'); fetchAll(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', 'error'); }
    } catch { showToast('Error', 'error'); }
  };

  const rejectRequest = async (requestId) => {
    try {
      await fetch(`${BACKEND_URL}/api/rewards/requests/${requestId}/reject`, { method: 'POST', headers });
      showToast(isEN ? 'Rejected' : 'Ditolak');
      fetchAll();
    } catch { }
  };

  const coins = gData?.coins || 0;
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const emojiOptions = ['🎁', '🍕', '🍦', '🎬', '🧸', '📱', '🎮', '🏃', '📝', '💺', '👑', '🌟', '🎨', '🚲', '📚', '🎵'];

  if (!user) return null;

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} gData={gData} />

      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{isEN ? 'Rewards' : 'Ganjaran'}</h1>
        <div className="header-coins"><Coins size={16} /> <span>{coins}</span></div>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div className={`shop-toast shop-toast-${toast.type}`}
              initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              {toast.msg}
            </motion.div>
          )}
        </AnimatePresence>

        {loading ? <div className="lb-loading"><div className="lb-spinner" /></div> : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>

            {/* Tabs */}
            <div className="aff-tabs" data-testid="reward-tabs">
              <button className={`aff-tab ${activeTab === 'browse' ? 'aff-tab-active' : ''}`}
                onClick={() => setActiveTab('browse')} data-testid="reward-tab-browse">
                <ShoppingBag size={14} /> {isEN ? 'Browse' : 'Lihat'}
              </button>
              <button className={`aff-tab ${activeTab === 'manage' ? 'aff-tab-active' : ''}`}
                onClick={() => setActiveTab('manage')} data-testid="reward-tab-manage">
                <Edit3 size={14} /> {isEN ? 'Manage' : 'Urus'}
              </button>
              <button className={`aff-tab ${activeTab === 'requests' ? 'aff-tab-active' : ''}`}
                onClick={() => setActiveTab('requests')} data-testid="reward-tab-requests">
                <Clock size={14} /> {isEN ? 'Requests' : 'Permintaan'}
                {pendingCount > 0 && <span className="rw-badge">{pendingCount}</span>}
              </button>
              <button className={`aff-tab ${activeTab === 'history' ? 'aff-tab-active' : ''}`}
                onClick={() => setActiveTab('history')} data-testid="reward-tab-history">
                <Star size={14} /> {isEN ? 'My Claims' : 'Tuntutan'}
              </button>
            </div>

            {/* Browse Tab — Student View */}
            {activeTab === 'browse' && (
              <div data-testid="reward-browse">
                <p className="rw-browse-desc">
                  {isEN ? 'Spend your coins on real rewards! Request a reward and wait for approval.' : 'Belanja syiling anda untuk ganjaran sebenar! Minta ganjaran dan tunggu kelulusan.'}
                </p>
                {browseData.rewards.length === 0 ? (
                  <div className="lb-empty">
                    {isEN ? 'No rewards available yet. Ask your parent or teacher to set up rewards!' : 'Tiada ganjaran lagi. Minta ibu bapa atau guru anda set up ganjaran!'}
                  </div>
                ) : (
                  <div className="rw-browse-grid">
                    {browseData.rewards.map((rw) => {
                      const canAfford = coins >= rw.cost;
                      const status = browseData.my_requests[rw.reward_id];
                      return (
                        <motion.div key={rw.reward_id} className="rw-browse-card" data-testid={`reward-browse-${rw.reward_id}`}
                          whileHover={{ y: -2 }}>
                          <div className="rw-browse-emoji">{rw.emoji}</div>
                          <div className="rw-browse-name">{isEN ? rw.name_en : (rw.name_bm || rw.name_en)}</div>
                          <div className="rw-browse-cost"><Coins size={14} /> {rw.cost}</div>
                          {rw.creator_name && <div className="rw-browse-by">{isEN ? 'by' : 'oleh'} {rw.creator_name}</div>}
                          {status === 'pending' ? (
                            <div className="rw-status rw-status-pending"><Clock size={14} /> {isEN ? 'Pending' : 'Menunggu'}</div>
                          ) : status === 'approved' ? (
                            <div className="rw-status rw-status-approved"><CheckCircle2 size={14} /> {isEN ? 'Approved!' : 'Diluluskan!'}</div>
                          ) : (
                            <button className={`rw-request-btn ${!canAfford ? 'rw-request-locked' : ''}`}
                              onClick={() => canAfford && requestReward(rw.reward_id)}
                              disabled={!canAfford}
                              data-testid={`reward-request-${rw.reward_id}`}>
                              {canAfford ? (isEN ? 'Request' : 'Minta') : (isEN ? 'Need more coins' : 'Perlu lebih syiling')}
                            </button>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Manage Tab — Parent/Teacher View */}
            {activeTab === 'manage' && (
              <div data-testid="reward-manage">
                <div className="rw-manage-header">
                  <p className="rw-browse-desc">
                    {isEN ? 'Create rewards for your children or students. They can redeem using their game coins!' : 'Cipta ganjaran untuk anak atau pelajar anda. Mereka boleh tebus guna syiling game!'}
                  </p>
                  <div className="rw-manage-btns">
                    <button className="rw-add-btn" onClick={() => setShowPresets(true)} data-testid="reward-add-preset">
                      <Star size={16} /> {isEN ? 'Quick Add' : 'Tambah Pantas'}
                    </button>
                    <button className="rw-add-btn rw-add-custom" onClick={() => setShowCreate(true)} data-testid="reward-add-custom">
                      <Plus size={16} /> {isEN ? 'Custom' : 'Tersuai'}
                    </button>
                  </div>
                </div>

                {/* My Rewards List */}
                {rewards.length === 0 ? (
                  <div className="lb-empty">
                    {isEN ? 'No rewards created yet. Add presets or create custom rewards!' : 'Tiada ganjaran lagi. Tambah preset atau cipta ganjaran tersuai!'}
                  </div>
                ) : (
                  <div className="rw-manage-list">
                    {rewards.map((rw) => (
                      <div key={rw.reward_id} className="rw-manage-item" data-testid={`reward-manage-${rw.reward_id}`}>
                        <span className="rw-manage-emoji">{rw.emoji}</span>
                        <div className="rw-manage-info">
                          <div className="rw-manage-name">{isEN ? rw.name_en : (rw.name_bm || rw.name_en)}</div>
                          <div className="rw-manage-meta">
                            <Coins size={12} /> {rw.cost} {isEN ? 'coins' : 'syiling'}
                            {rw.pending_requests > 0 && <span className="rw-pending-tag">{rw.pending_requests} {isEN ? 'pending' : 'menunggu'}</span>}
                            {rw.total_redeemed > 0 && <span className="rw-redeemed-tag">{rw.total_redeemed} {isEN ? 'redeemed' : 'ditebus'}</span>}
                          </div>
                        </div>
                        <button className="rw-delete-btn" onClick={() => deleteReward(rw.reward_id)} aria-label="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Preset Modal */}
                <AnimatePresence>
                  {showPresets && (
                    <motion.div className="rw-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      onClick={() => setShowPresets(false)}>
                      <motion.div className="rw-modal" initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
                        onClick={e => e.stopPropagation()}>
                        <div className="rw-modal-header">
                          <h3>{isEN ? 'Quick Add Rewards' : 'Tambah Ganjaran Pantas'}</h3>
                          <button onClick={() => setShowPresets(false)}><X size={20} /></button>
                        </div>
                        <p className="rw-modal-desc">{isEN ? 'Tap to add a preset reward' : 'Tekan untuk tambah ganjaran preset'}</p>
                        <div className="rw-preset-section">
                          <h4>{isEN ? 'For Parents (Home)' : 'Untuk Ibu Bapa (Rumah)'}</h4>
                          <div className="rw-preset-grid">
                            {presets.filter(p => p.category === 'home').map(p => {
                              const added = rewards.some(r => r.preset_id === p.id);
                              return (
                                <button key={p.id} className={`rw-preset-item ${added ? 'rw-preset-added' : ''}`}
                                  onClick={() => !added && addPreset(p.id)} disabled={added}>
                                  <span className="rw-preset-emoji">{p.emoji}</span>
                                  <span className="rw-preset-name">{isEN ? p.name_en : p.name_bm}</span>
                                  <span className="rw-preset-cost"><Coins size={11} /> {p.cost}</span>
                                  {added && <span className="rw-preset-check"><Check size={14} /></span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div className="rw-preset-section">
                          <h4>{isEN ? 'For Teachers (School)' : 'Untuk Guru (Sekolah)'}</h4>
                          <div className="rw-preset-grid">
                            {presets.filter(p => p.category === 'school').map(p => {
                              const added = rewards.some(r => r.preset_id === p.id);
                              return (
                                <button key={p.id} className={`rw-preset-item ${added ? 'rw-preset-added' : ''}`}
                                  onClick={() => !added && addPreset(p.id)} disabled={added}>
                                  <span className="rw-preset-emoji">{p.emoji}</span>
                                  <span className="rw-preset-name">{isEN ? p.name_en : p.name_bm}</span>
                                  <span className="rw-preset-cost"><Coins size={11} /> {p.cost}</span>
                                  {added && <span className="rw-preset-check"><Check size={14} /></span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Create Custom Modal */}
                <AnimatePresence>
                  {showCreate && (
                    <motion.div className="rw-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      onClick={() => setShowCreate(false)}>
                      <motion.div className="rw-modal" initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
                        onClick={e => e.stopPropagation()}>
                        <div className="rw-modal-header">
                          <h3>{isEN ? 'Create Custom Reward' : 'Cipta Ganjaran Tersuai'}</h3>
                          <button onClick={() => setShowCreate(false)}><X size={20} /></button>
                        </div>
                        <div className="rw-form">
                          <label>{isEN ? 'Emoji' : 'Emoji'}</label>
                          <div className="rw-emoji-picker">
                            {emojiOptions.map(e => (
                              <button key={e} className={`rw-emoji-btn ${newReward.emoji === e ? 'rw-emoji-active' : ''}`}
                                onClick={() => setNewReward(p => ({ ...p, emoji: e }))}>{e}</button>
                            ))}
                          </div>
                          <label>{isEN ? 'Reward Name (EN)' : 'Nama Ganjaran (EN)'}</label>
                          <input className="rw-input" value={newReward.name_en} placeholder={isEN ? 'e.g. Ice Cream Trip' : 'cth. Trip Aiskrim'}
                            onChange={e => setNewReward(p => ({ ...p, name_en: e.target.value }))} data-testid="reward-input-name" />
                          <label>{isEN ? 'Nama (BM)' : 'Nama (BM)'}</label>
                          <input className="rw-input" value={newReward.name_bm} placeholder={isEN ? 'Optional BM name' : 'Nama BM (pilihan)'}
                            onChange={e => setNewReward(p => ({ ...p, name_bm: e.target.value }))} />
                          <label>{isEN ? 'Cost (Coins)' : 'Kos (Syiling)'}</label>
                          <input className="rw-input" type="number" min="1" max="9999" value={newReward.cost}
                            onChange={e => setNewReward(p => ({ ...p, cost: Number(e.target.value) }))} data-testid="reward-input-cost" />
                          <button className="rw-create-btn" onClick={createReward} data-testid="reward-create-submit">
                            <Gift size={16} /> {isEN ? 'Create Reward' : 'Cipta Ganjaran'}
                          </button>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Requests Tab — Parent/Teacher Approval */}
            {activeTab === 'requests' && (
              <div data-testid="reward-requests">
                <p className="rw-browse-desc">
                  {isEN ? 'Approve or reject reward requests from students.' : 'Luluskan atau tolak permintaan ganjaran dari pelajar.'}
                </p>
                {requests.length === 0 ? (
                  <div className="lb-empty">{isEN ? 'No requests yet.' : 'Tiada permintaan lagi.'}</div>
                ) : (
                  <div className="rw-requests-list">
                    {requests.map((req) => (
                      <div key={req.request_id} className={`rw-request-item rw-request-${req.status}`} data-testid={`reward-req-${req.request_id}`}>
                        <span className="rw-request-emoji">{req.reward_emoji}</span>
                        <div className="rw-request-info">
                          <div className="rw-request-name">{req.student_name} {isEN ? 'wants' : 'mahu'}: <strong>{req.reward_name}</strong></div>
                          <div className="rw-request-cost"><Coins size={12} /> {req.reward_cost} {isEN ? 'coins' : 'syiling'}</div>
                        </div>
                        {req.status === 'pending' ? (
                          <div className="rw-request-actions">
                            <button className="rw-approve-btn" onClick={() => approveRequest(req.request_id)} data-testid={`approve-${req.request_id}`}>
                              <Check size={16} />
                            </button>
                            <button className="rw-reject-btn" onClick={() => rejectRequest(req.request_id)} data-testid={`reject-${req.request_id}`}>
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className={`rw-status rw-status-${req.status}`}>
                            {req.status === 'approved' ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                            {req.status === 'approved' ? (isEN ? 'Approved' : 'Diluluskan') : (isEN ? 'Rejected' : 'Ditolak')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* My Claims — Student History */}
            {activeTab === 'history' && (
              <div data-testid="reward-history">
                <p className="rw-browse-desc">
                  {isEN ? 'Your reward request history.' : 'Sejarah permintaan ganjaran anda.'}
                </p>
                {myRequests.length === 0 ? (
                  <div className="lb-empty">{isEN ? 'No claims yet. Browse rewards and start requesting!' : 'Tiada tuntutan lagi. Lihat ganjaran dan mula meminta!'}</div>
                ) : (
                  <div className="rw-requests-list">
                    {myRequests.map((req) => (
                      <div key={req.request_id} className={`rw-request-item rw-request-${req.status}`}>
                        <span className="rw-request-emoji">{req.reward_emoji}</span>
                        <div className="rw-request-info">
                          <div className="rw-request-name">{req.reward_name}</div>
                          <div className="rw-request-cost"><Coins size={12} /> {req.reward_cost}</div>
                        </div>
                        <div className={`rw-status rw-status-${req.status}`}>
                          {req.status === 'pending' && <><Clock size={14} /> {isEN ? 'Pending' : 'Menunggu'}</>}
                          {req.status === 'approved' && <><CheckCircle2 size={14} /> {isEN ? 'Approved!' : 'Diluluskan!'}</>}
                          {req.status === 'rejected' && <><XCircle size={14} /> {isEN ? 'Rejected' : 'Ditolak'}</>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: '20px' }}>
              &larr; {t('backToHome', language)}
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
